<script lang="ts" setup>
import { DnsRecord as DnsRecordSchema, type DnsRecord } from '@/types'
import { STATUS_CODES } from '@/constants'
import { DNS_TTL_PRESETS } from '@/constants/dnsPresets'
import { computed, defineAsyncComponent, onMounted, reactive, ref } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { uuid, useSelection, usePermissions } from '@/composables'
import { useNotifyStore } from '@/stores'
import DnsBulkActions from './DnsBulkActions.vue'
import { generateZoneFile, downloadZoneFile } from '@/utils/zoneFile'
import { formatNameForDisplay } from '@/utils/dnsName'
import { useDnsApiBase } from '@/composables/useDnsApiBase'
import { useI18n } from 'vue-i18n'

// Lazy load modal/slider components - only loaded when opened
const DnsBulkCreateSlider = defineAsyncComponent(() => import('./DnsBulkCreateSlider.vue'))
const DnsImportModal = defineAsyncComponent(() => import('./DnsImportModal.vue'))
const DnsTemplatesModal = defineAsyncComponent(() => import('./DnsTemplatesModal.vue'))
const DnsRecordDeleteModal = defineAsyncComponent(() => import('./DnsRecordDeleteModal.vue'))

// Direct import for SSR compatibility - async components can cause hydration issues
import DnsRecordAddEditSlider from './DnsRecordAddEditSlider.vue'

const { t } = useI18n()

const props = defineProps<{
  domainId: number
  domainName?: string
  disabled?: boolean
  isAdmin?: boolean
}>()

const emits = defineEmits<{
  (e: 'add', add: boolean): void
  (e: 'update', updateRecord: { state: boolean; record: DnsRecord }): void
  (e: 'delete', record: DnsRecord): void
}>()

const notifyStore = useNotifyStore()
const { canOnDomain, isAdmin: isGlobalAdmin } = usePermissions()

// Permission checks for the domain
const canWrite = computed(() => props.isAdmin || canOnDomain(props.domainId, 'dns:write').value || isGlobalAdmin.value)
const isReadOnly = computed(() => props.disabled || !canWrite.value)

const data = reactive({
  records: [] as DnsRecord[],
  loading: false
})

// Selection state via composable
const recordsRef = computed(() => data.records)
const {
  selectedKeys: selectedIds,
  isAllSelected,
  hasSelection,
  selectedItems: selectedRecords,
  toggleSelect,
  toggleSelectAll,
  clearSelection,
  removeKeys: removeSelectedIds
} = useSelection(recordsRef, (r: DnsRecord) => r.id)

// Import/Export state
const showImportModal = ref(false)
const showTemplatesModal = ref(false)
const showBulkCreateModal = ref(false)
const collapsed = ref(false)

// Add/Edit slider state (local like DnsBulkCreateSlider)
const dnsEditOpen = ref(false)
const currentEditRecord = ref<DnsRecord | null>(null)

// Owned here so admin + customer parents both get the delete dialog for free.
const recordToDelete = ref<DnsRecord | null>(null)

// Filter state
const searchQuery = ref('')
const typeFilter = ref<string>('all')
const recordTypes = ['all', 'A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA']

const filteredRecords = computed(() => {
  let result = data.records

  // Type filter
  if (typeFilter.value !== 'all') {
    result = result.filter((r) => r.type === typeFilter.value)
  }

  // Search filter (name only)
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((r) => r.name.toLowerCase().includes(query))
  }

  return result
})

const exportZone = () => {
  if (data.records.length === 0) return

  const domainFullName = props.domainName || `domain-${props.domainId}`
  const zoneRecords = data.records.map((r) => ({
    name: r.name,
    type: r.type,
    content: r.content,
    ttl: r.ttl,
    priority: r.priority
  }))

  const content = generateZoneFile(zoneRecords, domainFullName)
  const filename = `${domainFullName.replace(/\./g, '_')}.zone`
  downloadZoneFile(content, filename)
}

const openImport = () => {
  showImportModal.value = true
}

const closeImport = () => {
  showImportModal.value = false
}

const handleImported = () => {
  fetchRecords()
}

const openTemplates = () => {
  showTemplatesModal.value = true
}

const closeTemplates = () => {
  showTemplatesModal.value = false
}

const handleTemplatesApplied = () => {
  fetchRecords()
}

const openBulkCreate = () => {
  showBulkCreateModal.value = true
}

const closeBulkCreate = () => {
  showBulkCreateModal.value = false
}

const handleBulkCreated = (records: DnsRecord[]) => {
  // Add new records to local state
  data.records = [...data.records, ...records]
}

const formatTtl = (ttl: number | undefined): string => {
  if (!ttl) return '--'
  const preset = DNS_TTL_PRESETS.find((p) => p.value === ttl)
  if (preset) return t(preset.labelKey)
  if (ttl < 60) return t('dashboard.domains.dns.ttlPresets.seconds', { value: ttl })
  if (ttl < 3600) return t('dashboard.domains.dns.ttlPresets.minutes', { value: Math.floor(ttl / 60) })
  if (ttl < 86400) return t('dashboard.domains.dns.ttlPresets.hours', { value: Math.floor(ttl / 3600) })
  return t('dashboard.domains.dns.ttlPresets.days', { value: Math.floor(ttl / 86400) })
}

const formatName = (name: string): string => formatNameForDisplay(name, props.domainName)

const getRecordTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    A: 'blue',
    AAAA: 'indigo',
    CNAME: 'purple',
    MX: 'orange',
    TXT: 'green',
    NS: 'cyan',
    SRV: 'pink',
    CAA: 'amber',
    SOA: 'grey'
  }
  return colors[type] || 'grey'
}

// NS and SOA records are managed by the provider and should not be editable
const isProtectedRecord = (type: string): boolean => {
  return type === 'NS' || type === 'SOA'
}

const handleEditClick = (record: DnsRecord) => {
  currentEditRecord.value = record
  dnsEditOpen.value = true
}

const closeAddEditSlider = () => {
  dnsEditOpen.value = false
  currentEditRecord.value = null
}

const handleAddEditUpdate = (result: { record: DnsRecord; isNew: boolean }) => {
  if (result.isNew) {
    data.records = [...data.records, result.record]
  } else {
    data.records = data.records.map((r) => (r.id === result.record.id ? result.record : r))
  }
  closeAddEditSlider()
}

const handleDeleteClick = (record: DnsRecord) => {
  recordToDelete.value = record
}

const handleDeleted = (record: DnsRecord) => {
  data.records = data.records.filter((r) => r.id !== record.id)
  recordToDelete.value = null
  emits('delete', record)
}

const apiBase = useDnsApiBase(() => props.domainId, { isAdmin: () => props.isAdmin })

const fetchRecords = () => {
  data.loading = true
  useAxios<null, DnsRecord[]>('GET', `${apiBase.value}/records`, true)
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK) {
        const validate = DnsRecordSchema.array().safeParse(res.data)
        if (!validate.success) return
        data.records = Array.from(validate.data)
      }
    })
    .catch((error) => {
      handleAxiosError(error, { logPrefix: 'DnsRecords' })
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: t('errors.loadFailed'),
        message: getErrorMessage(error)
      })
    })
    .finally(() => {
      data.loading = false
    })
}

const refreshRecords = () => {
  fetchRecords()
}

// Local state update methods (no server refetch)
const addRecord = (record: DnsRecord) => {
  data.records = [...data.records, record]
}

const updateRecord = (record: DnsRecord) => {
  const index = data.records.findIndex((r) => r.id === record.id)
  if (index !== -1) {
    data.records[index] = record
    data.records = [...data.records]
  }
}

const removeRecord = (id: number) => {
  data.records = data.records.filter((r) => r.id !== id)
  removeSelectedIds([id])
}

const removeRecords = (ids: number[]) => {
  const idSet = new Set(ids)
  data.records = data.records.filter((r) => !idSet.has(r.id))
  removeSelectedIds(ids)
}

const updateRecordsTtl = (ids: number[], ttl: number) => {
  data.records = data.records.map((r) => (ids.includes(r.id) ? { ...r, ttl } : r))
}

defineExpose({ refreshRecords, addRecord, updateRecord, removeRecord, removeRecords, updateRecordsTtl })

onMounted(() => {
  fetchRecords()
})
</script>

<template>
  <div class="dns-records-container" :class="{ collapsed }">
    <!-- External NS Warning (hidden for admins) -->
    <div v-if="disabled && !isAdmin" class="external-ns-warning">
      <v-icon icon="mdi-alert-circle-outline" size="20" />
      <div class="warning-content">
        <span class="warning-title">{{ $t('dashboard.domains.dns.externalNsWarningTitle') }}</span>
        <span class="warning-text">{{ $t('dashboard.domains.dns.externalNsWarningText') }}</span>
      </div>
    </div>

    <!-- Section Header -->
    <div class="section-header" @click="collapsed = !collapsed">
      <div class="header-left">
        <button class="collapse-toggle" :class="{ collapsed }">
          <v-icon icon="mdi-chevron-down" size="20" />
        </button>
        <div class="header-icon">
          <v-icon icon="mdi-dns" size="18" />
        </div>
        <div class="header-text">
          <h2 class="section-title">{{ $t('dashboard.domains.dns.title') }}</h2>
          <p class="section-subtitle">{{ $t('dashboard.domains.dns.subtitle', { domain: domainName || '' }) }}</p>
        </div>
        <v-chip v-if="collapsed && data.records.length > 0" size="small" variant="tonal" class="count-badge">
          {{ data.records.length }}
        </v-chip>
      </div>
      <div class="header-actions" @click.stop>
        <button class="action-btn secondary" :title="$t('dashboard.domains.dns.templates')" :disabled="isReadOnly" @click="openTemplates">
          <v-icon icon="mdi-file-document-multiple-outline" size="18" />
          <span class="btn-text">{{ $t('dashboard.domains.dns.templates') }}</span>
        </button>
        <button class="action-btn secondary" title="Export" @click="exportZone">
          <v-icon icon="mdi-download" size="18" />
          <span class="btn-text">Export</span>
        </button>
        <button class="action-btn secondary" :title="$t('dashboard.domains.dns.import')" :disabled="isReadOnly" @click="openImport">
          <v-icon icon="mdi-upload" size="18" />
          <span class="btn-text">{{ $t('dashboard.domains.dns.import') }}</span>
        </button>
        <button class="action-btn secondary" :title="$t('dashboard.domains.dns.bulkActions')" :disabled="isReadOnly" @click="openBulkCreate">
          <v-icon icon="mdi-playlist-plus" size="18" />
          <span class="btn-text">Bulk</span>
        </button>
        <button class="action-btn primary" :disabled="isReadOnly" @click="dnsEditOpen = true">
          <v-icon icon="mdi-plus" size="18" />
          <span class="btn-text">{{ $t('dashboard.domains.dns.add') }}</span>
        </button>
      </div>
    </div>

    <!-- Collapsible Content -->
    <div v-show="!collapsed" class="collapsible-content">
      <!-- Filter Bar -->
      <div v-if="data.records.length > 0" class="filter-bar">
        <div class="search-wrapper">
          <v-icon icon="mdi-magnify" size="18" class="search-icon" />
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="$t('dashboard.domains.overview.search')"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="clear-btn"
            @click="searchQuery = ''"
          >
            <v-icon icon="mdi-close" size="14" />
          </button>
        </div>
        <div class="type-filters">
          <button
            v-for="type in recordTypes"
            :key="type"
            type="button"
            class="type-btn"
            :class="{ active: typeFilter === type }"
            @click="typeFilter = type"
          >
            {{ type === 'all' ? $t('common.all') : type }}
          </button>
        </div>
        <label class="checkbox-wrapper select-all-mobile">
          <input
            type="checkbox"
            :checked="isAllSelected"
            :indeterminate="hasSelection && !isAllSelected"
            @change="toggleSelectAll"
          />
          <span class="checkbox-custom" />
          <span class="select-all-label">{{ $t('dashboard.domains.dns.selectAll') }}</span>
        </label>
      </div>

      <!-- Loading State -->
      <div v-if="data.loading" class="loading-state">
        <v-progress-circular indeterminate size="32" width="2" color="primary" />
        <span>{{ $t('dashboard.domains.dns.loading') }}</span>
      </div>

    <!-- Records Table. Below 768px every row is re-laid as a card with CSS
         grid, which drops the implicit table semantics, hence the explicit
         roles. -->
    <div v-else-if="filteredRecords.length > 0" class="records-table-wrapper">
      <table class="records-table" role="table">
        <thead role="rowgroup">
          <tr role="row">
            <th class="col-select" scope="col" role="columnheader">
              <label class="checkbox-wrapper">
                <input
                  type="checkbox"
                  :checked="isAllSelected"
                  :indeterminate="hasSelection && !isAllSelected"
                  @change="toggleSelectAll"
                />
                <span class="checkbox-custom" />
              </label>
            </th>
            <th class="col-name" scope="col" role="columnheader">{{ $t('dashboard.domains.dns.name') }}</th>
            <th class="col-type" scope="col" role="columnheader">{{ $t('dashboard.domains.dns.type') }}</th>
            <th class="col-content" scope="col" role="columnheader">{{ $t('dashboard.domains.dns.value') }}</th>
            <th class="col-ttl" scope="col" role="columnheader">{{ $t('dashboard.domains.dns.ttl') }}</th>
            <th class="col-priority" scope="col" role="columnheader">{{ $t('dashboard.domains.dns.priority') }}</th>
            <th class="col-actions" scope="col" role="columnheader">{{ $t('dashboard.domains.dns.actions') }}</th>
          </tr>
        </thead>
        <tbody role="rowgroup">
          <tr
            v-for="record in filteredRecords"
            :key="record.id"
            class="record-row"
            :class="{ selected: selectedIds.has(record.id) }"
            role="row"
          >
            <td class="col-select" role="cell">
              <label
                class="checkbox-wrapper"
                :class="{ disabled: isProtectedRecord(record.type) }"
                :title="isProtectedRecord(record.type) ? $t('dashboard.domains.dns.protectedRecord') : ''"
                @click.stop
              >
                <input
                  type="checkbox"
                  :checked="selectedIds.has(record.id)"
                  :disabled="isProtectedRecord(record.type)"
                  @change="toggleSelect(record.id)"
                />
                <span class="checkbox-custom" />
              </label>
            </td>
            <td class="col-name" role="cell">
              <code>{{ formatName(record.name) }}</code>
            </td>
            <td class="col-type" role="cell">
              <v-chip :color="getRecordTypeColor(record.type)" size="small" variant="tonal">
                {{ record.type }}
              </v-chip>
            </td>
            <td class="col-content" role="cell">
              <span class="content-text">{{ record.content }}</span>
            </td>
            <td class="col-ttl" role="cell" :data-label="$t('dashboard.domains.dns.ttl')">
              {{ formatTtl(record.ttl) }}
            </td>
            <td class="col-priority" role="cell" :data-label="$t('dashboard.domains.dns.priority')">
              {{ record.priority !== undefined ? record.priority : '--' }}
            </td>
            <td class="col-actions" role="cell">
              <button
                class="icon-btn"
                :title="isProtectedRecord(record.type) ? $t('dashboard.domains.dns.protectedRecord') : $t('dashboard.domains.dns.edit')"
                :disabled="isProtectedRecord(record.type)"
                @click.prevent="handleEditClick(record)"
              >
                <v-icon icon="mdi-pencil" size="18" />
              </button>
              <button
                class="icon-btn danger"
                :title="isProtectedRecord(record.type) ? $t('dashboard.domains.dns.protectedRecord') : $t('dashboard.domains.dns.delete')"
                :disabled="isProtectedRecord(record.type)"
                @click.prevent="handleDeleteClick(record)"
              >
                <v-icon icon="mdi-trash-can" size="18" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      <!-- No Filter Results -->
      <div v-else-if="data.records.length > 0 && filteredRecords.length === 0" class="empty-state filter-empty">
        <div class="empty-icon">
          <v-icon icon="mdi-filter-remove-outline" size="36" />
        </div>
        <h3 class="empty-title">{{ $t('dashboard.domains.dns.noFilterResults') }}</h3>
        <p class="empty-text">
          {{ $t('dashboard.domains.dns.noFilterResultsText') }}
        </p>
        <button type="button" class="reset-filter-btn" @click="searchQuery = ''; typeFilter = 'all'">
          {{ $t('dashboard.domains.dns.resetFilter') }}
        </button>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-icon">
          <v-icon icon="mdi-dns-outline" size="40" />
        </div>
        <h3 class="empty-title">{{ $t('dashboard.domains.dns.noRecords') }}</h3>
        <p class="empty-text">
          {{ $t('dashboard.domains.dns.noRecordsText') }}
        </p>
      </div>
    </div>

    <!-- Bulk Actions Toolbar -->
    <DnsBulkActions
      v-if="hasSelection"
      :selected-count="selectedIds.size"
      :selected-records="selectedRecords"
      :domain-id="domainId"
      :api-base="apiBase"
      @clear-selection="clearSelection"
      @records-deleted="removeRecords"
      @records-updated="(payload) => updateRecordsTtl(payload.ids, payload.ttl)"
    />

    <!-- Import Modal -->
    <DnsImportModal
      :show="showImportModal"
      :domain-id="domainId"
      :domain-name="domainName || ''"
      :api-base="apiBase"
      @close="closeImport"
      @imported="handleImported"
    />

    <!-- Templates Modal -->
    <DnsTemplatesModal
      :show="showTemplatesModal"
      :domain-id="domainId"
      :domain-name="domainName || ''"
      :api-base="apiBase"
      @close="closeTemplates"
      @applied="handleTemplatesApplied"
    />

    <!-- Bulk Create Slider (teleported to body to escape overflow:hidden) -->
    <Teleport to="body">
      <DnsBulkCreateSlider
        v-if="showBulkCreateModal"
        :show="showBulkCreateModal"
        :domain-id="domainId"
        :domain-name="domainName || ''"
        :api-base="apiBase"
        @close="closeBulkCreate"
        @created="handleBulkCreated"
      />
    </Teleport>

    <!-- Add/Edit Slider (teleported to body to escape overflow:hidden) -->
    <Teleport to="body">
      <DnsRecordAddEditSlider
        v-if="dnsEditOpen"
        :show="dnsEditOpen"
        :edit-record="currentEditRecord"
        :domain-id="domainId"
        :domain-name="domainName || ''"
        :api-base="apiBase"
        @add-update="handleAddEditUpdate"
        @close-drawer="closeAddEditSlider"
      />
    </Teleport>

    <DnsRecordDeleteModal
      v-if="recordToDelete"
      :record="recordToDelete"
      :show-modal="true"
      :domain-id="domainId"
      :api-base="apiBase"
      @deleted="handleDeleted"
      @hide-modal="recordToDelete = null"
    />
  </div>
</template>

<style lang="scss" scoped>
.dns-records-container {
  background: rgba(15, 23, 48, 0.5);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

/* Section Header */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.03);
  }

  .collapsed & {
    border-bottom: none;
  }
}

.collapse-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 4px;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.8);
  }

  .v-icon {
    transition: transform 0.2s ease;
  }

  &.collapsed .v-icon {
    transform: rotate(-90deg);
  }
}

.count-badge {
  margin-left: 12px;
}

.collapsible-content {
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(33, 150, 243, 0.08));
  border: 1px solid rgba(33, 150, 243, 0.2);
  border-radius: 10px;
  color: #64b5f6;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 2px;
}

.section-subtitle {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  &.primary {
    background: rgba(76, 175, 80, 0.15);
    border: 1px solid rgba(76, 175, 80, 0.25);
    color: #81c784;

    &:hover:not(:disabled) {
      background: rgba(76, 175, 80, 0.25);
      border-color: rgba(76, 175, 80, 0.4);
    }
  }

  &.secondary {
    background: rgba(33, 150, 243, 0.12);
    border: 1px solid rgba(33, 150, 243, 0.2);
    color: #64b5f6;

    &:hover:not(:disabled) {
      background: rgba(33, 150, 243, 0.2);
      border-color: rgba(33, 150, 243, 0.35);
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

/* External NS Warning */
.external-ns-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(255, 152, 0, 0.08);
  border-bottom: 1px solid rgba(255, 152, 0, 0.15);
  border-radius: 12px 12px 0 0;

  .v-icon {
    color: #ffb74d;
    flex-shrink: 0;
    margin-top: 2px;
  }
}

.warning-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.warning-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #ffb74d;
}

.warning-text {
  font-size: 0.8rem;
  color: rgba(255, 183, 77, 0.7);
  line-height: 1.4;
}

/* Filter Bar */
.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(15, 23, 48, 0.3);
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
}

.search-wrapper {
  position: relative;
  flex: 1;
  max-width: 280px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(200, 218, 255, 0.4);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 8px 36px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
  outline: none;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    border-color: rgba(0, 176, 255, 0.4);
    background: rgba(15, 23, 48, 0.7);
  }
}

.clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: rgba(200, 218, 255, 0.1);
  border: none;
  border-radius: 50%;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(200, 218, 255, 0.2);
    color: rgba(200, 218, 255, 0.8);
  }
}

.type-filters {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.type-btn {
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.6);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.12);
    color: rgba(200, 218, 255, 0.8);
  }

  &.active {
    background: rgba(0, 176, 255, 0.15);
    border-color: rgba(0, 176, 255, 0.4);
    color: #00b0ff;
  }
}

.reset-filter-btn {
  margin-top: 16px;
  padding: 8px 16px;
  background: rgba(0, 176, 255, 0.12);
  border: 1px solid rgba(0, 176, 255, 0.25);
  border-radius: 8px;
  color: #00b0ff;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 176, 255, 0.2);
    border-color: rgba(0, 176, 255, 0.4);
  }
}

.filter-empty {
  padding: 40px 24px;
}

/* Loading State */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 24px;
  color: rgba(200, 218, 255, 0.6);
}

/* Records Table */
.records-table-wrapper {
  overflow: auto;
  height: 600px;
}

.records-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: left;
    padding: 10px 12px;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(200, 218, 255, 0.5);
    background: rgba(15, 23, 48, 0.3);
    border-bottom: 1px solid rgba(120, 163, 255, 0.08);
  }

  .col-select {
    width: 48px;
    text-align: center;
    padding: 10px 8px;
  }
  .col-name {
    width: 14%;
  }
  .col-type {
    width: 9%;
  }
  .col-content {
    width: 38%;
  }
  .col-ttl {
    width: 11%;
    text-align: center;
  }
  .col-priority {
    width: 9%;
    text-align: center;
  }
  .col-actions {
    width: 12%;
    text-align: right;
  }
}

/* Custom Checkbox */

/* Both this and its media-query counterpart are qualified with
   .checkbox-wrapper: the bare class ties with the wrapper's own display rule
   and would lose to whichever of the two is written last. */
.checkbox-wrapper.select-all-mobile {
  display: none;
}

.select-all-label {
  margin-left: 8px;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.7);
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  input[type='checkbox'] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  .checkbox-custom {
    width: 18px;
    height: 18px;
    background: rgba(255, 255, 255, 0.05);
    border: 2px solid rgba(120, 163, 255, 0.25);
    border-radius: 4px;
    transition: all 0.15s ease;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      left: 5px;
      top: 1px;
      width: 5px;
      height: 10px;
      border: solid #00b0ff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg) scale(0);
      transition: transform 0.15s ease;
    }
  }

  input:checked + .checkbox-custom {
    background: rgba(0, 176, 255, 0.15);
    border-color: rgba(0, 176, 255, 0.5);

    &::after {
      transform: rotate(45deg) scale(1);
    }
  }

  input:indeterminate + .checkbox-custom {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.4);

    &::after {
      left: 3px;
      top: 6px;
      width: 10px;
      height: 2px;
      border: none;
      background: #00b0ff;
      transform: none;
    }
  }

  &:hover:not(.disabled) .checkbox-custom {
    border-color: rgba(0, 176, 255, 0.5);
    background: rgba(0, 176, 255, 0.08);
  }

  &.disabled {
    cursor: not-allowed;

    .checkbox-custom {
      opacity: 0.3;
    }
  }
}

.record-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(120, 163, 255, 0.04);
  }

  &.selected {
    background: rgba(0, 176, 255, 0.08);

    &:hover {
      background: rgba(0, 176, 255, 0.12);
    }
  }

  td {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
    vertical-align: middle;
  }
}

code {
  background: rgba(120, 163, 255, 0.1);
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  color: rgba(200, 218, 255, 0.9);
  word-break: break-all;
  display: inline-block;
  line-height: 1.4;
}

.content-text {
  display: inline-block;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.9);
  }

  &.danger:hover:not(:disabled) {
    background: rgba(244, 67, 54, 0.15);
    color: #ef5350;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 60px 24px;
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  background: rgba(120, 163, 255, 0.08);
  border-radius: 18px;
  color: rgba(200, 218, 255, 0.3);
  margin-bottom: 20px;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 8px;
}

.empty-text {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
  max-width: 320px;
}

/* Light Theme */
.v-theme--lightTheme {
  .dns-records-container {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .section-title,
  .empty-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .section-subtitle,
  .empty-text {
    color: rgba(15, 23, 42, 0.6);
  }

  .records-table {
    th {
      color: rgba(15, 23, 42, 0.6);
      background: rgba(15, 23, 42, 0.03);
    }
  }

  .record-row td {
    color: rgba(15, 23, 42, 0.85);
  }

  code {
    background: rgba(28, 110, 227, 0.08);
    color: rgba(15, 23, 42, 0.9);
  }

  .filter-bar {
    background: rgba(15, 23, 42, 0.03);
  }

  .search-input {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.9);

    &::placeholder {
      color: rgba(15, 23, 42, 0.4);
    }

    &:focus {
      background: rgba(255, 255, 255, 0.95);
      border-color: rgba(28, 110, 227, 0.4);
    }
  }

  .type-btn {
    background: rgba(28, 110, 227, 0.05);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.6);

    &:hover {
      background: rgba(28, 110, 227, 0.1);
      color: rgba(15, 23, 42, 0.8);
    }

    &.active {
      background: rgba(28, 110, 227, 0.15);
      border-color: rgba(28, 110, 227, 0.4);
      color: #1c6ee3;
    }
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 768px) {
  .filter-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .search-wrapper {
    max-width: none;
  }

  .type-filters {
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 4px;
  }

  .type-btn {
    flex-shrink: 0;
  }

  .header-actions {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;

    .action-btn {
      padding: 8px 12px;

      .btn-text {
        display: none;
      }
    }

    .action-btn.primary {
      flex: 1;
      min-width: 140px;

      .btn-text {
        display: inline;
      }
    }
  }

  /* The table cannot fit a phone: .content-text alone claims 300px of
     non-wrapping width, so the action column ends up outside the nested
     scroller and edit/delete are unreachable (the "DNS is not editable on
     mobile" report). Re-lay every row as a card instead, which also brings TTL
     and priority back rather than hiding them. */
  .records-table-wrapper {
    height: auto;
    overflow: visible;
    padding: 12px;
  }

  .checkbox-wrapper.select-all-mobile {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
  }

  .records-table {
    display: block;

    thead {
      display: none;
    }

    tbody {
      display: block;
    }

    td {
      display: block;
      padding: 0;
      text-align: left;
    }

    /* The desktop .col-* rules outrank the td block above (a class beats an
       element), so the column widths and padding have to be reset per column. */
    .col-select,
    .col-name,
    .col-type,
    .col-content,
    .col-ttl,
    .col-priority,
    .col-actions {
      width: auto;
    }

    .col-select {
      grid-area: select;
      align-self: start;
      padding: 0;
    }

    .col-name {
      grid-area: name;
      min-width: 0;

      code {
        max-width: 100%;
        font-size: 0.8rem;
      }
    }

    .col-type {
      grid-area: type;
    }

    .col-content {
      grid-area: value;
      min-width: 0;
    }

    .col-ttl {
      grid-area: ttl;
      text-align: left;
    }

    .col-priority {
      grid-area: prio;
      text-align: right;
    }

    .col-ttl,
    .col-priority {
      font-size: 0.72rem;
      color: rgba(200, 218, 255, 0.55);

      /* The header row is gone, so each value carries its own label. */
      &::before {
        content: attr(data-label) ' ';
        text-transform: uppercase;
        letter-spacing: 0.04em;
        opacity: 0.7;
      }
    }

    .col-actions {
      grid-area: actions;
      display: flex;
      gap: 6px;
      justify-content: flex-end;
    }
  }

  .record-row {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr) auto auto;
    grid-template-areas:
      'select name  type  actions'
      'select value value value'
      'select ttl   prio  prio';
    column-gap: 8px;
    row-gap: 6px;
    align-items: center;
    padding: 12px;
    margin-bottom: 10px;
    border: 1px solid rgba(120, 163, 255, 0.12);
    border-radius: 10px;
    background: rgba(15, 23, 48, 0.35);

    &:last-child {
      margin-bottom: 0;
      /* The desktop rule drops the border on the last row; a card keeps it. */
      border-bottom: 1px solid rgba(120, 163, 255, 0.12);
    }
  }

  .content-text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    max-width: none;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
    font-size: 0.82rem;
    line-height: 1.45;
  }

  /* :hover never fires on touch, so at rest these icons were indistinguishable
     from their own :disabled state. Give them a resting frame instead. */
  .icon-btn {
    flex: 0 0 auto;
    width: 36px;
    height: 36px;
    background: rgba(120, 163, 255, 0.08);
    border: 1px solid rgba(120, 163, 255, 0.18);
    color: rgba(200, 218, 255, 0.85);
  }

  .v-theme--lightTheme {
    .record-row {
      border-color: rgba(28, 110, 227, 0.14);
      background: rgba(15, 23, 42, 0.02);

      &.selected {
        background: rgba(28, 110, 227, 0.1);
      }
    }

    .icon-btn {
      background: rgba(28, 110, 227, 0.06);
      border-color: rgba(28, 110, 227, 0.16);
      color: rgba(15, 23, 42, 0.75);
    }
  }
}
</style>
